import "./CampaignDistribution.css";

import { InlineSpinner } from "../../components/InlineSpinner";
import Plot from "react-plotly.js";
import PropTypes from "prop-types";
import React from "react";

// Upload-session status buckets → colours (mirrors UploadCounter). Exported so
// the campaign distribution and a single site build the exact same donut.
export const UPLOAD_BUCKETS = [
  { label: "In progress", color: "#f3a64a", statuses: [0, 1] },
  { label: "Partial", color: "#ffcb3d", statuses: [4] },
  { label: "Completed", color: "#cdd3d6", statuses: [2] },
  { label: "Synchronized", color: "#56c596", statuses: [3] },
];

// Turn a status-count map (e.g. {0: 2, 3: 5, total: 7}) into donut segments,
// dropping empty buckets so the legend stays honest.
export const uploadSegments = (counts = {}) =>
  UPLOAD_BUCKETS.map((bucket) => ({
    label: bucket.label,
    value: bucket.statuses.reduce(
      (acc, status) => acc + (counts[status] ?? 0),
      0
    ),
    color: bucket.color,
    filterValue: bucket.label,
  })).filter((segment) => segment.value > 0);

// Presentational donut + legend for a set of {label, value, color} segments.
// `tabs` is optional content rendered between the title and the chart — the
// campaign view passes its dimension toggle there; a single site has none.
export const DistributionDonut = ({
  title,
  headerAction,
  segments,
  unit = "sites",
  accentColor,
  tabs,
  loading,
  onSelect,
  size = 172,
}) => {
  const total = segments.reduce((acc, segment) => acc + segment.value, 0);

  const data = [
    {
      type: "pie",
      hole: 0.7,
      sort: false,
      direction: "clockwise",
      labels: segments.map((segment) => segment.label),
      values: segments.map((segment) => segment.value),
      marker: {
        colors: segments.map((segment) => segment.color),
        line: { color: "#ffffff", width: 2 },
      },
      textinfo: "none",
      // No hover bubble: it repeated the legend word for word and Plotly drew
      // it inside the donut's own SVG, so it came out truncated. The legend is
      // the readout; a slice just lightens on hover (CSS).
      // "none" and not "skip": both suppress the label, but "skip" also skips
      // the pick layer, and the slices need to stay clickable.
      hoverinfo: "none",
    },
  ];

  const layout = {
    width: size,
    height: size,
    margin: { l: 0, r: 0, t: 0, b: 0 },
    paper_bgcolor: "transparent",
    plot_bgcolor: "transparent",
    showlegend: false,
    // Centre shows the total being broken down (e.g. "35 SITES"). Fonts scale
    // with the donut so a smaller size keeps the total inside the hole.
    annotations: [
      {
        text: `<b>${total}</b>`,
        x: 0.5,
        y: 0.54,
        showarrow: false,
        font: { color: "#184456", size: Math.round(26 * (size / 172)) },
      },
      {
        text: unit.toUpperCase(),
        x: 0.5,
        y: 0.41,
        showarrow: false,
        font: { color: "#9aa0a6", size: Math.round(9 * (size / 172)) },
      },
    ],
  };

  const config = { displayModeBar: false, responsive: false };

  // Clicking a slice does exactly what clicking its legend row does. Plotly
  // hands back the index into `labels`, which lines up with `segments` because
  // the trace is `sort: false`, so no label matching is needed (and labels are
  // display text: a country is "France", its filter value is "FRA").
  const handleSliceClick = (event) => {
    const segment = segments[event?.points?.[0]?.pointNumber];
    if (segment?.filterValue != null) onSelect(segment.filterValue);
  };

  return (
    <section
      className="cf-dist"
      style={accentColor ? { "--cf-accent": accentColor } : undefined}
    >
      <header className="cf-dist__head">
        <h3 className="cf-dist__title">{title}</h3>
        {headerAction}
      </header>

      {loading ? (
        <div
          className="cf-dist__body"
          style={{
            justifyContent: "center",
            alignItems: "center",
            minHeight: size,
          }}
        >
          <InlineSpinner className="fa-2x" />
        </div>
      ) : (
        <>
          {tabs}

          {segments.length === 0 ? (
            <div className="cf-dist__empty">
              <i className="fa-solid fa-file-lines" />
              <span>No data</span>
            </div>
          ) : (
            <div className="cf-dist__body">
              <div
                className={"cf-dist__chart" + (onSelect ? " is-clickable" : "")}
              >
                <Plot
                  data={data}
                  layout={layout}
                  config={config}
                  onClick={onSelect ? handleSliceClick : undefined}
                />
              </div>
              <ul className="cf-dist__legend">
                {segments.map((segment) => {
                  const clickable =
                    Boolean(onSelect) && segment.filterValue != null;
                  const select = () => onSelect(segment.filterValue);
                  return (
                    <li
                      className={
                        "cf-dist__row" + (clickable ? " is-clickable" : "")
                      }
                      key={segment.label}
                      onClick={clickable ? select : undefined}
                      role={clickable ? "button" : undefined}
                      tabIndex={clickable ? 0 : undefined}
                      onKeyDown={
                        clickable
                          ? (event) => {
                              if (event.key === "Enter" || event.key === " ") {
                                event.preventDefault();
                                select();
                              }
                            }
                          : undefined
                      }
                      title={
                        clickable ? `Filter by ${segment.label}` : undefined
                      }
                    >
                      <span
                        className="cf-dist__dot"
                        style={{ "--dot": segment.color }}
                      />
                      <span className="cf-dist__name" title={segment.label}>
                        {segment.label}
                      </span>
                      <span className="cf-dist__val">{segment.value}</span>
                      <span className="cf-dist__pct">
                        {total ? Math.round((segment.value / total) * 100) : 0}%
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </>
      )}
    </section>
  );
};

DistributionDonut.propTypes = {
  title: PropTypes.string.isRequired,
  headerAction: PropTypes.node,
  segments: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.number.isRequired,
      color: PropTypes.string.isRequired,
      filterValue: PropTypes.string,
    })
  ).isRequired,
  unit: PropTypes.string,
  accentColor: PropTypes.string,
  tabs: PropTypes.node,
  loading: PropTypes.bool,
  onSelect: PropTypes.func,
  size: PropTypes.number,
};
